import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import type { Rol } from "../lib/types";

interface SessionProfile {
  rol: Rol;
  nombre: string;
  apoderadoId?: string;
}

interface AuthState {
  user: User | null;
  profile: SessionProfile | null;
  loading: boolean;
  loginAdmin: (email: string, password: string) => Promise<void>;
  requestMagicLink: (rutAlumno: string, email: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  activarApoderado: (rutAlumno: string, fechaNacimiento: string, email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<SessionProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u) {
        setProfile(null);
        setLoading(false);
        return;
      }
      try {
        const tokenResult = await u.getIdTokenResult();
        if (tokenResult.claims.rol === "apoderado") {
          setProfile({
            rol: "apoderado",
            nombre: (u.displayName as string) || "Apoderado",
            apoderadoId: tokenResult.claims.apoderadoId as string,
          });
        } else {
          // Admin: perfil en /usuarios/{uid}.
          const snap = await getDoc(doc(db, "usuarios", u.uid));
          const data = snap.data();
          setProfile({
            rol: (data?.rol as Rol) || "admin",
            nombre: (data?.nombre as string) || u.email || "Administrador",
          });
        }
      } catch {
        setProfile({ rol: "admin", nombre: u.email || "Administrador" });
      } finally {
        setLoading(false);
      }
    });
  }, []);

  const loginAdmin = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  // Paso 1: solicita el enlace mágico (el Worker valida RUT+correo y envía el email).
  const requestMagicLink = useCallback(async (rutAlumno: string, email: string) => {
    const res = await fetch("/api/auth/apoderado/request", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ rutAlumno, email }),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(data.error || "No se pudo enviar el enlace");
    }
  }, []);

  // Paso 2: canjea el token del enlace por una sesión de Firebase.
  const verifyMagicLink = useCallback(async (token: string) => {
    const res = await fetch("/api/auth/apoderado/verify", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = (await res.json()) as { token?: string; error?: string };
    if (!res.ok || !data.token) throw new Error(data.error || "No se pudo iniciar sesión");
    await signInWithCustomToken(auth, data.token);
  }, []);

  // Activación de cuenta: prueba identidad (RUT + fecha nac del alumno), registra correo y da acceso.
  const activarApoderado = useCallback(async (rutAlumno: string, fechaNacimiento: string, email: string) => {
    const res = await fetch("/api/auth/apoderado/activar", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ rutAlumno, fechaNacimiento, email }),
    });
    const data = (await res.json()) as { token?: string; error?: string };
    if (!res.ok || !data.token) throw new Error(data.error || "No se pudo activar la cuenta");
    await signInWithCustomToken(auth, data.token);
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, profile, loading, loginAdmin, requestMagicLink, verifyMagicLink, activarApoderado, logout }),
    [user, profile, loading, loginAdmin, requestMagicLink, verifyMagicLink, activarApoderado, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}
